import { UCODE_KEY } from '@/common/constants';

interface PendingEvent {
  callback: () => Promise<void>;
  timestamp: number;
}

class UcodeManager {
  private static instance: UcodeManager;
  private pendingEvents: PendingEvent[] = [];
  private listeners: Set<(ucode: string | null) => void> = new Set();
  private checkInterval: NodeJS.Timeout | null = null;
  private lastUcode: string | null = null;

  private constructor() {
    this.lastUcode = localStorage.getItem(UCODE_KEY);
    this.startMonitoring();
  }

  static getInstance(): UcodeManager {
    if (!UcodeManager.instance) {
      UcodeManager.instance = new UcodeManager();
    }
    return UcodeManager.instance;
  }

  getUcode(): string | null {
    return localStorage.getItem(UCODE_KEY);
  }

  setUcode(ucode: string): void {
    localStorage.setItem(UCODE_KEY, ucode);
    this.lastUcode = ucode;
    this.notifyListeners(ucode);
    this.processPendingEvents();
  }

  queueEventIfNoUcode(callback: () => Promise<void>): void {
    const ucode = this.getUcode();
    if (!ucode) {
      this.pendingEvents.push({
        callback,
        timestamp: Date.now(),
      });
      this.cleanOldEvents();
      this.ensureMonitoring();
    } else {
      callback().catch((error) => {
        console.error('Erro ao executar evento:', error);
      });
    }
  }

  private startMonitoring(): void {
    this.checkInterval = setInterval(() => {
      const currentUcode = localStorage.getItem(UCODE_KEY);
      if (currentUcode !== this.lastUcode) {
        this.lastUcode = currentUcode;
        if (currentUcode) {
          this.notifyListeners(currentUcode);
          this.processPendingEvents();
        }
      }

      // Stop polling when idle to save CPU
      if (this.listeners.size === 0 && this.pendingEvents.length === 0 && this.checkInterval) {
        clearInterval(this.checkInterval);
        this.checkInterval = null;
      }
    }, 1000);

    window.addEventListener('storage', this.handleStorageChange);
  }

  private handleStorageChange = (e: StorageEvent): void => {
    if (e.key === UCODE_KEY && e.newValue) {
      this.lastUcode = e.newValue;
      this.notifyListeners(e.newValue);
      this.processPendingEvents();
    }
  };

  private processPendingEvents(): void {
    if (this.pendingEvents.length === 0) return;

    const events = [...this.pendingEvents];
    this.pendingEvents = [];

    events.forEach(({ callback }) => {
      callback().catch((error) => {
        console.error('Erro ao processar evento pendente:', error);
      });
    });
  }

  private cleanOldEvents(): void {
    const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
    this.pendingEvents = this.pendingEvents.filter((event) => event.timestamp > fiveMinutesAgo);
  }

  subscribe(listener: (ucode: string | null) => void): () => void {
    this.listeners.add(listener);
    this.ensureMonitoring();
    return () => {
      this.listeners.delete(listener);
    };
  }

  private ensureMonitoring(): void {
    if (!this.checkInterval) {
      this.startMonitoring();
    }
  }

  private notifyListeners(ucode: string | null): void {
    this.listeners.forEach((listener) => listener(ucode));
  }

  destroy(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }
    window.removeEventListener('storage', this.handleStorageChange);
    this.listeners.clear();
    this.pendingEvents = [];
  }
}

export const ucodeManager = UcodeManager.getInstance();
